/**
 * A small in-memory cache for Home's reads (owner instruction, 2026-10-01).
 *
 * Home read its recordings, both baselines and any Daily Status from nothing on
 * every visit, so each click on Home showed "Loading your overview…" and drew
 * the whole body again. With this, a revisit renders the last answers at once
 * and the same reads run again quietly; an answer equal to the last one keeps
 * the SAME object, so React has nothing to re-render and nothing on the page is
 * drawn twice.
 *
 * ── WHAT IT IS NOT ──────────────────────────────────────────────────────────
 *
 * - Not storage. It is a module-level Map: it lives as long as this tab's page
 *   and is gone on reload. Nothing reaches localStorage or another tab.
 * - Not shared between people. Every key a caller builds carries the user id,
 *   and `clearSessionCache` runs on sign-out and on an expired session
 *   (`AuthContext`), so §4.5's "clear user-specific cached data on sign-out"
 *   still holds by construction.
 * - Not a source of truth. A cached answer is shown only while the same read is
 *   running again; that read's answer (or its error) replaces it.
 * - Bounded: the oldest entry goes past `MAX_ENTRIES`.
 */

const MAX_ENTRIES = 32
const store = new Map<string, { value: unknown; json: string }>()

/** The last answer stored under `key`, or `undefined` when there is none. */
export function cacheRead<T>(key: string | null | undefined): T | undefined {
  if (key === null || key === undefined) return undefined
  const hit = store.get(key)
  return hit === undefined ? undefined : (hit.value as T)
}

/**
 * Store `value` under `key` and return what the caller should render: the
 * PREVIOUS object when the new answer is equal to it (compared as JSON, which is
 * what the API sent), so an unchanged refresh changes no reference.
 */
export function cacheWrite<T>(key: string, value: T): T {
  let json: string
  try {
    json = JSON.stringify(value) ?? 'undefined'
  } catch {
    store.delete(key)
    return value
  }
  const previous = store.get(key)
  if (previous !== undefined && previous.json === json) return previous.value as T
  store.delete(key)
  store.set(key, { value, json })
  while (store.size > MAX_ENTRIES) {
    const oldest = store.keys().next().value
    if (oldest === undefined) break
    store.delete(oldest)
  }
  return value
}

/** Forget every answer. Sign-out and an expired session call this. */
export function clearSessionCache(): void {
  store.clear()
}
